import React, { createContext, useContext, useState, useCallback } from 'react';
import {
  Snackbar,
  Alert,
  AlertTitle,
  IconButton,
  Box,
  LinearProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface Notification {
  id: string;
  type: NotificationType;
  title?: string;
  message: string;
  duration?: number;
  showProgress?: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface NotificationContextType {
  showNotification: (notification: Omit<Notification, 'id'>) => void;
  showSuccess: (message: string, title?: string) => void;
  showError: (message: string, title?: string) => void;
  showWarning: (message: string, title?: string) => void;
  showInfo: (message: string, title?: string) => void;
  hideNotification: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
  maxNotifications?: number;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
  maxNotifications = 3
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const showNotification = useCallback((notification: Omit<Notification, 'id'>) => {
    const id = `notification-${Date.now()}-${Math.random()}`;
    const newNotification: Notification = {
      ...notification,
      id,
      duration: notification.duration ?? 6000
    };

    setNotifications(prev => {
      const updated = [...prev, newNotification];
      // Keep only the most recent notifications
      return updated.slice(-maxNotifications);
    });

    // Auto-hide after duration
    if (newNotification.duration && newNotification.duration > 0) {
      setTimeout(() => {
        hideNotification(id);
      }, newNotification.duration);
    }
  }, [maxNotifications]);

  const hideNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const showSuccess = useCallback((message: string, title?: string) => {
    showNotification({
      type: 'success',
      title: title || 'Success',
      message,
      duration: 4000
    });
  }, [showNotification]);

  const showError = useCallback((message: string, title?: string) => {
    showNotification({
      type: 'error',
      title: title || 'Error',
      message,
      duration: 8000
    });
  }, [showNotification]);

  const showWarning = useCallback((message: string, title?: string) => {
    showNotification({
      type: 'warning',
      title: title || 'Warning',
      message,
      duration: 6000
    });
  }, [showNotification]);

  const showInfo = useCallback((message: string, title?: string) => {
    showNotification({
      type: 'info',
      title: title || 'Info',
      message,
      duration: 5000
    });
  }, [showNotification]);

  const value: NotificationContextType = {
    showNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    hideNotification
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <NotificationContainer notifications={notifications} onClose={hideNotification} />
    </NotificationContext.Provider>
  );
};

interface NotificationContainerProps {
  notifications: Notification[];
  onClose: (id: string) => void;
}

const NotificationContainer: React.FC<NotificationContainerProps> = ({
  notifications,
  onClose
}) => {
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 80,
        right: 24,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        maxWidth: 400,
        width: '100%'
      }}
    >
      {notifications.map((notification) => (
        <NotificationItem
          key={notification.id}
          notification={notification}
          onClose={() => onClose(notification.id)}
        />
      ))}
    </Box>
  );
};

interface NotificationItemProps {
  notification: Notification;
  onClose: () => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onClose
}) => {
  const [progress, setProgress] = useState(100);

  React.useEffect(() => {
    if (notification.showProgress && notification.duration) {
      const interval = 50; // Update every 50ms
      const decrement = (interval / notification.duration) * 100;
      
      const timer = setInterval(() => {
        setProgress(prev => {
          const next = prev - decrement;
          return next <= 0 ? 0 : next;
        });
      }, interval);

      return () => clearInterval(timer);
    }
  }, [notification.showProgress, notification.duration]);

  const getIcon = () => {
    switch (notification.type) {
      case 'success':
        return <SuccessIcon />;
      case 'error':
        return <ErrorIcon />;
      case 'warning':
        return <WarningIcon />;
      case 'info':
        return <InfoIcon />;
      default:
        return null;
    }
  };

  return (
    <Alert
      severity={notification.type}
      icon={getIcon()}
      action={
        <Box display="flex" alignItems="center" gap={1}>
          {notification.action && (
            <IconButton
              size="small"
              onClick={() => {
                notification.action!.onClick();
                onClose();
              }}
              sx={{ color: 'inherit' }}
            >
              {notification.action.label}
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: 'inherit' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      }
      sx={{
        width: '100%',
        boxShadow: 3,
        '& .MuiAlert-message': {
          width: '100%'
        }
      }}
    >
      {notification.title && (
        <AlertTitle sx={{ fontWeight: 'bold' }}>
          {notification.title}
        </AlertTitle>
      )}
      {notification.message}
      {notification.showProgress && (
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            mt: 1,
            height: 3,
            borderRadius: 1,
            backgroundColor: 'rgba(255,255,255,0.3)'
          }}
        />
      )}
    </Alert>
  );
};
